import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import "@/i18n";   // side-effect: initialises i18next before any component reads t()
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { DirectionProvider } from "@/contexts/DirectionContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import AppLayout from "@/layout/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Income from "@/pages/Income";
import Expenses from "@/pages/Expenses";
import Loans from "@/pages/Loans";
import Subscriptions from "@/pages/Subscriptions";
import Bills from "@/pages/Bills";
import Goals from "@/pages/Goals";
import Investments from "@/pages/Investments";
import Budget from "@/pages/Budget";
import Reports from "@/pages/Reports";
import Optimizer from "@/pages/Optimizer";
import Advisor from "@/pages/Advisor";
import Wrapup from "@/pages/Wrapup";
import Streak from "@/pages/Streak";
import Circles from "@/pages/Circles";
import Connections from "@/pages/Connections";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import ShareHub from "@/pages/ShareHub";
import Settings from "@/pages/Settings";
import WallOfWins from "@/pages/WallOfWins";
import AdminTrials from "@/pages/AdminTrials";
import AdminSuperDashboard from "@/pages/AdminSuperDashboard";
import AdminUserLookup from "@/pages/AdminUserLookup";
import AdminWaitlist from "@/pages/AdminWaitlist";
import AdminAudit from "@/pages/AdminAudit";
import Diag from "@/pages/Diag";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
        <div className="text-[var(--text-muted)] text-sm tracking-widest uppercase">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

/**
 * AdminOnly — gates admin routes so normal users don't even see them.
 * `user.is_admin` is derived server-side from the ADMIN_EMAILS env var
 * on every /auth/me call, so this check can't be spoofed client-side.
 * Non-admin logged-in users are silently redirected to /dashboard.
 */
function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
        <div className="text-[var(--text-muted)] text-sm tracking-widest uppercase">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

/**
 * ProOnly — gates ADVANCED features (Reports, Optimizer, AI Advisor,
 * Streak, Circles, Wrap-up, Share hub). Trial + Free users typing a
 * Pro URL directly are bounced to /pricing so they can upgrade;
 * sidebar clicks are already handled via the lock icon in AppLayout.
 */
function ProOnly({ children }) {
  const { user, loading, trialStatus } = useAuth();
  const [status, setStatus] = useState(null);
  // Admins bypass the Pro gate entirely — they see every feature
  // unlocked so they can demo, debug, and support the app end-to-end.
  const isAdmin = !!user?.is_admin;
  useEffect(() => {
    let cancelled = false;
    if (!user || isAdmin) return;
    // Trust auth context: is_pro comes back on trialStatus too, but
    // /billing/status is the authoritative source.
    import("@/lib/api").then(({ default: api }) => {
      api.get("/billing/status").then(({ data }) => {
        if (!cancelled) setStatus(data);
      }).catch(() => { if (!cancelled) setStatus({ is_pro: false }); });
    });
    return () => { cancelled = true; };
  }, [user, trialStatus, isAdmin]);
  if (loading || (user && !isAdmin && status === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
        <div className="text-[var(--text-muted)] text-sm tracking-widest uppercase">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (isAdmin) return children;
  if (!status?.is_pro) return <Navigate to="/pricing" replace state={{ reason: "pro_only_route" }} />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous session_id check to prevent race conditions
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/wall" element={<WallOfWins />} />
      <Route path="/dashboard" element={<Protected><AppLayout><Dashboard /></AppLayout></Protected>} />
      <Route path="/income" element={<Protected><AppLayout><Income /></AppLayout></Protected>} />
      <Route path="/expenses" element={<Protected><AppLayout><Expenses /></AppLayout></Protected>} />
      <Route path="/loans" element={<Protected><AppLayout><Loans /></AppLayout></Protected>} />
      <Route path="/subscriptions" element={<Protected><AppLayout><Subscriptions /></AppLayout></Protected>} />
      <Route path="/bills" element={<Protected><AppLayout><Bills /></AppLayout></Protected>} />
      <Route path="/goals" element={<Protected><AppLayout><Goals /></AppLayout></Protected>} />
      <Route path="/investments" element={<Protected><AppLayout><Investments /></AppLayout></Protected>} />
      <Route path="/budget" element={<Protected><AppLayout><Budget /></AppLayout></Protected>} />
      <Route path="/reports" element={<Protected><ProOnly><AppLayout><Reports /></AppLayout></ProOnly></Protected>} />
      <Route path="/optimizer" element={<Protected><ProOnly><AppLayout><Optimizer /></AppLayout></ProOnly></Protected>} />
      <Route path="/advisor" element={<Protected><ProOnly><AppLayout><Advisor /></AppLayout></ProOnly></Protected>} />
      <Route path="/wrapup" element={<Protected><ProOnly><AppLayout><Wrapup /></AppLayout></ProOnly></Protected>} />
      <Route path="/streak" element={<Protected><ProOnly><AppLayout><Streak /></AppLayout></ProOnly></Protected>} />
      <Route path="/circles" element={<Protected><ProOnly><AppLayout><Circles /></AppLayout></ProOnly></Protected>} />
      <Route path="/connections" element={<Protected><AppLayout><Connections /></AppLayout></Protected>} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
      <Route path="/share" element={<Protected><ProOnly><AppLayout><ShareHub /></AppLayout></ProOnly></Protected>} />
      <Route path="/settings" element={<Protected><AppLayout><Settings /></AppLayout></Protected>} />
      <Route path="/admin/trials" element={<AdminOnly><AppLayout><AdminTrials /></AppLayout></AdminOnly>} />
      <Route path="/admin/metrics" element={<AdminOnly><AppLayout><AdminSuperDashboard /></AppLayout></AdminOnly>} />
      <Route path="/admin/users" element={<AdminOnly><AppLayout><AdminUserLookup /></AppLayout></AdminOnly>} />
      <Route path="/admin/waitlist" element={<AdminOnly><AppLayout><AdminWaitlist /></AppLayout></AdminOnly>} />
      <Route path="/admin/audit" element={<AdminOnly><AppLayout><AdminAudit /></AppLayout></AdminOnly>} />
      <Route path="/diag" element={<AdminOnly><Diag /></AdminOnly>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <DirectionProvider>
            <LanguageProvider>
              <AppRouter />
              <ThemedToaster />
            </LanguageProvider>
          </DirectionProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster position="top-right" richColors theme={theme} />;
}
